import { useSelector } from 'react-redux';
import CloseCircle from 'assets/img/close-circle-bold.svg';
import TickCircle from 'assets/img/tick-circle-bold.svg';
import classNames from 'classnames';

import { CallStatus } from '../../lib/enums';
import { CompanyLogoAndNameState } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { TypedObject } from '../../lib/util/types';

import CompanyLogo from '../CompanyLogo/CompanyLogo';

import styles from './ProcessFinishedModal.module.scss';

type Props = {
    symbol: string;
    processFailedMessage: string;
    processFailedMessageSub?: string;
    processSuccessMessageTitle: string;
    callStatus?: CallStatus;
    defaultMessageBody?: string;
    defaultMessageTitle?: string;
    processPendingMessage?: { title: string, body: string };
    processPartiallyRejectedMessage?: { title: string, body: string };
    processRejectedMessage?: { title: string, body: string };
    processSuccessMessageBody?: string;
}

const ModalBody = ({
  symbol,
  callStatus,
  defaultMessageBody,
  processFailedMessage,
  processFailedMessageSub,
  defaultMessageTitle,
  processPendingMessage,
  processSuccessMessageBody,
  processSuccessMessageTitle,
  processPartiallyRejectedMessage,
  processRejectedMessage,
} : Props) => {
  const companyLogoAndName = useSelector<RootState, TypedObject<CompanyLogoAndNameState>>(
    (state: RootState) => state.fundamental.companyLogoAndName,
  );
  const logo = companyLogoAndName[symbol]?.logo;
  const url = companyLogoAndName[symbol]?.url;

  const errorMessageRender = () => {
    if (processPartiallyRejectedMessage) {
      return (
        <>
          <h6 className={styles.title}>{processPartiallyRejectedMessage.title}</h6>
          <p className={styles.message}>{processPartiallyRejectedMessage.body}</p>
        </>
      );
    }

    return (
      <>
        <h6 className={styles.errorTitle}>{processFailedMessage}</h6>
        {processFailedMessageSub && <h6 className={styles.errorTitle}>{processFailedMessageSub}</h6>}
      </>
    );
  };
  const getModalBody = (): JSX.Element => {
    if (callStatus === CallStatus.READY) {
      return (
        <>
          <div className={styles.symbolLogoWrapper}>
            <img src={TickCircle} alt="check success" className={styles.icon} />
            <CompanyLogo url={url} logo={logo} className={styles.logo} symbol={symbol} />
          </div>
          <h6 className={styles.title}>{processSuccessMessageTitle}</h6>
          {processSuccessMessageBody && <p className={styles.message}>{processSuccessMessageBody}</p>}
        </>
      );
    }
    if (processPendingMessage) {
      return (
        <>
          <div className={styles.symbolLogoWrapper}>
            <img src={TickCircle} alt="check success" className={styles.icon} />
            <CompanyLogo url={url} logo={logo} className={styles.logo} symbol={symbol} />
          </div>
          <h6 className={styles.title}>{processPendingMessage.title}</h6>
          <p className={styles.message}>{processPendingMessage.body}</p>
        </>
      );
    }
    if (defaultMessageTitle) {
      return (
        <>
          <div className={styles.symbolLogoWrapper}>
            <img src={TickCircle} alt="check success" className={styles.icon} />
            <CompanyLogo url={url} logo={logo} className={styles.logo} symbol={symbol} />
          </div>
          <h6 className={styles.title}>{defaultMessageTitle}</h6>
          {defaultMessageBody && <p className={styles.message}>{defaultMessageBody}</p>}
        </>
      );
    }

    if (processRejectedMessage) {
      return (
        <div className={styles.rejected}>
          <div className={classNames(styles.symbolLogoWrapper, styles.rejectedLogo)}>
            <img src={CloseCircle} alt="rejected order" className={styles.icon} />
            <CompanyLogo url={url} logo={logo} className={styles.logo} symbol={symbol} />
          </div>
          <h6 className={styles.rejectedTitle}>{processRejectedMessage.title}</h6>
          <p className={styles.rejectedMessage}>{processRejectedMessage.body}</p>
        </div>
      );
    }

    return (
      <div className={styles.errorWrapper}>
        <div className={styles.symbolLogoWrapper}>
          <img src={CloseCircle} alt="rejected order" className={styles.icon} />
          <CompanyLogo url={url} logo={logo} className={styles.logo} symbol={symbol} />
        </div>
        {errorMessageRender()}
      </div>
    );
  };

  return getModalBody();
};

export default ModalBody;
